/**
 * Contract status helper functions
 * Provides utilities for calculating effective contract status based on dates
 */
import { vatBreakdown } from "@dallali/shared";

export interface ContractStatusInput {
  status: string;
  end_date: string | null;
}

/**
 * Calculates the effective status of a contract.
 * If a contract has status "active" but its end_date has passed,
 * this returns "expired" instead.
 * 
 * This ensures correct display even if the backend cron hasn't run yet.
 */
export function getEffectiveStatus(contract: ContractStatusInput): string {
  // If contract is active but end_date has passed, treat as expired
  if (
    contract.status === "active" &&
    contract.end_date
  ) {
    const endDate = new Date(contract.end_date);
    const today = new Date();
    // Set both to midnight for fair comparison
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    
    if (endDate < today) {
      return "expired";
    }
  }
  
  return contract.status;
}

/**
 * Checks if a contract should be considered expired based on its end date.
 */
export function isContractExpired(endDate: string | null): boolean {
  if (!endDate) return false;

  const end = new Date(endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  return end < today;
}

/**
 * Determines whether a contract can be edited.
 *
 * Manager policy (2026-05-12): only draft contracts are editable. Once a
 * contract is activated — and especially once an Ejar registration or
 * gateway-recorded payment exists — no field on the contract or its invoices
 * may be modified. Admin users do NOT bypass this rule; the database RPC
 * update_contract_with_invoices enforces the same gate.
 *
 * The `isAdmin` parameter is retained for call-site compatibility but is
 * intentionally ignored.
 */
export function canEditContract(contractStatus: string, _isAdmin: boolean): boolean {
  return contractStatus === "draft";
}

export type LeaseType = "residential" | "commercial";

// Saudi VAT rule: residential leases are exempt, everything else is taxed.
// The classifier is `units.purpose` — NOT `properties.purpose` and NOT
// `property_type`. A multi_purpose building / compound can host residential
// apartments AND commercial offices in the same property; ZATCA applies VAT
// at the LEASE level (per unit), so a residential apartment inside a
// multi_purpose compound is exempt regardless of the property's bucket.
// The helper signature stays generic — pass whichever purpose value matches
// the lease being classified (callers should pass `unit.purpose`).
export function getLeaseType(purpose: string | null | undefined): LeaseType {
  return purpose === "residential" ? "residential" : "commercial";
}

export function isRentVatApplicable(purpose: string | null | undefined): boolean {
  return getLeaseType(purpose) === "commercial";
}

// Convert a FeeConfig-style entry (type: "fixed" | "percentage", value:
// string) into a final numeric amount for persistence.
//
// "fixed"      → the value passes through as-is (rounded to 2 decimals).
// "percentage" → totalAmount × (value / 100), 2-decimal rounded.
//
// Empty strings, non-numeric values, negatives, and (for percentage) a
// missing/non-positive totalAmount all collapse to 0. The contract edit
// drawer seeds every fee from the contract row on open, so untouched
// fields round-trip their original numeric value; a deliberately blanked
// field arrives as 0 and clears the column (the RPC distinguishes "send 0"
// from "send NULL" for that reason).
export function resolveFeeAmount(
  fee: { type: string; value: string },
  totalAmount: number | string | null | undefined,
): number {
  const v = Number(fee.value);
  if (!Number.isFinite(v) || v < 0) return 0;
  if (fee.type === "percentage") {
    const total = typeof totalAmount === "number" ? totalAmount : Number(totalAmount);
    if (!Number.isFinite(total) || total <= 0) return 0;
    return Math.round(total * (v / 100) * 100) / 100;
  }
  return Math.round(v * 100) / 100;
}

// ── Auto-split payment schedule ───────────────────────────────────────────────

import { addMonths, addYears, addDays, intervalToDuration, format } from "date-fns";
import type { PaymentScheduleItem } from "@/types/contracts";

// ── Ejar-style Y/M/D contract duration ──────────────────────────────────────

/** Years/months/days breakdown, as displayed by Ejar's duration step. */
export interface DurationYMD {
  years: number;
  months: number;
  days: number;
}

/**
 * Ejar-style INCLUSIVE end date: start + years + months + days − 1 day.
 *
 * Saudi rental contracts are end-inclusive (see `fullTermTotal`'s
 * exclusive-end reasoning below) — a 3-year lease starting 2026-08-01 runs
 * THROUGH 2029-07-31, not through 2029-08-01. Matches real EJAR contracts,
 * e.g. Aug 1 2026 + 3y0m0d → Jul 31 2029.
 *
 * Returns "" when the duration is all-zero (nothing to add) or startDate is
 * missing/invalid — callers should treat that as "no duration entered yet"
 * rather than writing a nonsensical end date one day before the start.
 */
export function addDurationInclusive(startDate: string, duration: DurationYMD): string {
  if (!startDate) return "";
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return "";

  const years = Number.isFinite(duration.years) ? duration.years : 0;
  const months = Number.isFinite(duration.months) ? duration.months : 0;
  const days = Number.isFinite(duration.days) ? duration.days : 0;
  if (years <= 0 && months <= 0 && days <= 0) return "";

  // addYears/addMonths clamp month-end overflow (Jan 31 + 1 month → Feb 28/29)
  // and handle leap years correctly — never hand-roll this arithmetic.
  let end = addYears(start, years);
  end = addMonths(end, months);
  end = addDays(end, days);
  end = addDays(end, -1); // inclusive rental convention
  return format(end, "yyyy-MM-dd");
}

/**
 * Reverse of `addDurationInclusive`: decompose the inclusive span
 * [startDate, endDate] into {years, months, days} — the way Ejar's duration
 * step displays an existing contract's dates (e.g. ١٩ سنوات ٤ شهور ٢٧ أيام).
 *
 * Works on the EXCLUSIVE end (endDate + 1 day) so that, for whole-unit
 * durations, `diffDurationInclusive(start, addDurationInclusive(start, d))`
 * round-trips back to `d`.
 */
export function diffDurationInclusive(startDate: string, endDate: string): DurationYMD {
  const zero: DurationYMD = { years: 0, months: 0, days: 0 };
  if (!startDate || !endDate) return zero;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return zero;

  const exclusiveEnd = addDays(end, 1);
  if (exclusiveEnd <= start) return zero;

  const dur = intervalToDuration({ start, end: exclusiveEnd });
  return {
    years: dur.years ?? 0,
    months: dur.months ?? 0,
    days: dur.days ?? 0,
  };
}

/**
 * Whether a Y/M/D duration has actually been entered — i.e. at least one of
 * the three fields holds a non-zero value. A naive `!!value` check is wrong
 * here: `!!"0"` is `true` (a non-empty string), so typing "0" into "years"
 * and leaving months/days blank would count as "duration entered" even
 * though the duration is all-zero. That falsely routes a subsequent
 * start_date change through the duration-recompute branch instead of the
 * manual-dates branch, and `addDurationInclusive` correctly returns "" for
 * an all-zero duration — leaving `endDate` stale instead of recomputed
 * (PR #3069 review). Values are always digit-only strings (DurationFields
 * strips non-digits) so a plain `"0"` string comparison is exact — no need
 * for `Number(v) !== 0`.
 */
export function isDurationEntered(years: string, months: string, days: string): boolean {
  return [years, months, days].some((v) => !!v && v !== "0");
}

/**
 * Exact contract duration in months from a start/end date pair, for the
 * LEGACY `durationValue`/`durationUnit` fields that
 * `generateAutoSplitSchedule`'s payment-count math still reads (see
 * `ContractWizardDrawer`'s `contractMonths`). The Y/M/D duration fields are UI
 * sugar over start_date/end_date — this keeps that downstream schedule math
 * correct however the user arrived at the dates (typed Y/M/D, or edited the
 * dates directly), without touching any of that consuming code.
 *
 * Uses `intervalToDuration` (the same exact decomposition `diffDurationInclusive`
 * uses) rather than a days/30 approximation — a days/30 approximation
 * over/undercounts whole-year spans that straddle a leap year (e.g. the 1096
 * days in "2026-08-01" -> "2029-07-31" rounds to 37, not 36), which inflates
 * `generateAutoSplitSchedule`'s payment count by one period (PR #3069 review).
 * Any leftover day fraction rounds UP to the next month, matching
 * `generateAutoSplitSchedule`'s own ceiling math on `contractMonths / 12`.
 */
export function contractMonthsFromDates(startDate: string, endDate: string): number {
  if (!startDate || !endDate) return 12;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 12;
  const exclusiveEnd = addDays(end, 1);
  if (exclusiveEnd <= start) return 12;
  const dur = intervalToDuration({ start, end: exclusiveEnd });
  const months = (dur.years ?? 0) * 12 + (dur.months ?? 0);
  const daysRemainder = dur.days ?? 0;
  return Math.max(1, months + (daysRemainder > 0 ? 1 : 0));
}

/**
 * Canonical payments-PER-YEAR for each standard payment frequency.
 *
 * SINGLE SOURCE OF TRUTH. Both the create wizard (AddContractDrawer) and the
 * edit wizard (EditContractDrawer / PaymentInfoStep) must derive
 * `paymentsPerYear` from the contract's `payment_frequency` via this map —
 * NEVER from the persisted `payments_count`.
 *
 * Why: `payments_count` is stored as the TOTAL number of invoices across the
 * whole term (e.g. a 17-year semi-annual contract stores 34, = 2 × 17). Feeding
 * that total back into `generateAutoSplitSchedule` as if it were the per-year
 * rate re-multiplies by the year count (34 × 17 = 578 invoices) — the schedule
 * "explosion" regression. Keying off the frequency keeps the edit-form regen
 * identical to what was originally saved.
 */
export const PAYMENTS_PER_YEAR_BY_FREQUENCY: Record<string, number> = {
  monthly: 12,
  quarterly: 4,
  "semi-annually": 2,
  annually: 1,
  custom: 0,
};

/**
 * Resolve payments-per-year from a payment_frequency string. Falls back to
 * quarterly (4) for unknown/missing values — the historical wizard default —
 * so a legacy contract with no frequency still regenerates a sane schedule.
 * `custom` returns 0 (caller uses the user-defined schedule instead).
 */
export function paymentsPerYearForFrequency(frequency: string | null | undefined): number {
  if (frequency === "custom") return 0;
  const v = frequency ? PAYMENTS_PER_YEAR_BY_FREQUENCY[frequency] : undefined;
  return v ?? 4;
}

/** The discrete payments-per-year values the wizard's selector offers. */
export const VALID_PAYMENTS_PER_YEAR = [1, 2, 3, 4, 6, 12] as const;

/**
 * Recover payments-PER-YEAR from a contract's STORED full-term payment count.
 *
 * THE BUG A FIX. `contracts.payments_count` is persisted as the TOTAL number
 * of invoices across the whole term (= paymentsPerYear × contractYears, e.g.
 * 34 for a 17-year semi-annual contract). When the edit drawer opens it must
 * convert that total back to a per-year rate before feeding it to
 * `generateAutoSplitSchedule` — otherwise the generator re-multiplies by the
 * year count (ceil(years × total)) and the schedule explodes
 * (17 × 34 = 578 invoices instead of 34).
 *
 *   paymentsPerYear = round(storedTotalCount / contractYears)
 *
 * The result is snapped to the nearest value the selector actually offers
 * (1/2/3/4/6/12) so odd or hand-edited legacy data still maps to a real
 * option. Falls back to 4 (quarterly) when the total/months are unusable.
 */
export function recoverPaymentsPerYear(
  storedTotalCount: number,
  contractMonths: number,
): number {
  const months = Number.isFinite(contractMonths) && contractMonths > 0 ? contractMonths : 12;
  const years = Math.max(1, Math.round(months / 12));
  if (!Number.isFinite(storedTotalCount) || storedTotalCount <= 0) return 4;
  const raw = Math.round(storedTotalCount / years);
  const exact = VALID_PAYMENTS_PER_YEAR.find((v) => v === raw);
  if (exact) return exact;
  return VALID_PAYMENTS_PER_YEAR.reduce((best, v) =>
    Math.abs(v - raw) < Math.abs(best - raw) ? v : best,
  );
}

/**
 * Generate an equal-split payment schedule distributing a GIVEN canonical total
 * exactly across all payment periods — no penny drift.
 *
 * The caller is responsible for computing the correct `canonicalTotal`:
 *   - rentBasis 'term_total': canonicalTotal = enteredAmount (no proration).
 *   - rentBasis 'annual':     canonicalTotal = fullTermTotal(annualRent, term).
 *
 * Amounts are distributed via `splitAmountEvenly` so the per-payment amounts
 * (each at 2 dp) sum back to `canonicalTotal` EXACTLY — the last payment absorbs
 * any halala remainder.
 *
 * @param canonicalTotal  - The full-term total to distribute (ex-VAT).
 * @param paymentsPerYear - Number of payment periods per year (1 / 2 / 4 / 12).
 * @param contractMonths  - Total contract duration in months.
 * @param startDate       - ISO date string "YYYY-MM-DD" for the first payment due date.
 * @returns Ordered payment schedule items ready for the RPC.
 */
export function generateAutoSplitSchedule(
  canonicalTotal: number,
  paymentsPerYear: number,
  contractMonths: number,
  startDate: string,
): PaymentScheduleItem[] {
  if (canonicalTotal <= 0 || paymentsPerYear <= 0 || contractMonths <= 0 || !startDate) return [];

  const totalPayments = Math.ceil((contractMonths / 12) * paymentsPerYear);
  const amounts = splitAmountEvenly(canonicalTotal, totalPayments);
  const monthsPerPayment = 12 / paymentsPerYear;

  const start = new Date(startDate);
  return amounts.map((amount, i) => ({
    paymentNumber: i + 1,
    dueDate: format(addMonths(start, i * monthsPerPayment), "yyyy-MM-dd"),
    amount,
  }));
}

/**
 * Compute the FULL-TERM contract total (what payments should sum to), with the
 * final partial period PRORATED — matching REGA's 364-day (52-week) year.
 *
 * The old implementation used `annualRent × ceil(contractMonths / 12)`, which
 * billed a whole extra year for any partial final period. For a 2yr+11mo
 * contract that over-targets by ~one year of rent and shows a false "still
 * owed" balance in the flexible-payment reconciliation. REGA instead charges
 * full years at the annual rate plus a pro-rata slice of the final partial
 * year.
 *
 * Two call shapes:
 *
 *   fullTermTotal(annualRent, contractMonths)
 *     Month-precision proration (no dates available). Final partial period is
 *     prorated by (remainingMonths / 12).
 *
 *   fullTermTotal(annualRent, { startDate, endDate })
 *     Day-precision proration (preferred — matches REGA closest). Final partial
 *     period is prorated by (remainingDays / 364) using the actual dates.
 *
 * Examples:
 *   fullTermTotal(10_000, 12)  → 10_000      (exactly 1 year)
 *   fullTermTotal(10_000, 24)  → 20_000      (exactly 2 years)
 *   fullTermTotal(10_000, 18)  → 15_000      (1 year + 6/12 → 1.5 × annual)
 *   fullTermTotal(312_220.8, { startDate: "2026-11-11", endDate: "2029-10-10" })
 *                              → ≈ 910_9xx   (2 full years + ~11mo prorated /364)
 */
export function fullTermTotal(
  annualRent: number,
  term: number | { startDate: string; endDate: string },
): number {
  if (annualRent <= 0) return annualRent;

  // ── Date-precision path (preferred) ─────────────────────────────────────
  if (typeof term === "object") {
    const { startDate, endDate } = term;
    if (!startDate || !endDate) return annualRent;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
      return annualRent;
    }

    // Saudi real estate contracts (EJAR/REGA) are END-INCLUSIVE: a lease
    // "2026-02-14 → 2043-02-13" runs
    // through the whole of Feb 13, so it is exactly 17 years (the day BEFORE
    // the 17th anniversary). Work in "exclusive end" terms by treating the
    // billable boundary as end + 1 day; then a year [anniv, anniv+1yr) is
    // complete when anniv+1yr <= exclusiveEnd.
    const MS_PER_DAY = 1000 * 60 * 60 * 24;
    const exclusiveEnd = end.getTime() + MS_PER_DAY;

    // Count complete years by advancing the anniversary date, so leap years
    // and varying month lengths never drift the boundary.
    let fullYears = 0;
    const anniversary = new Date(start);
    while (true) {
      const next = new Date(start);
      next.setFullYear(start.getFullYear() + fullYears + 1);
      if (next.getTime() <= exclusiveEnd) {
        fullYears += 1;
        anniversary.setFullYear(start.getFullYear() + fullYears);
      } else {
        break;
      }
    }

    // Remaining partial period: from the last anniversary to the exclusive end.
    // REGA uses a 364-day (52-week) year for partial-period proration, so we
    // divide by 364 rather than 365 to match the REGA standard.
    const remainingDays = Math.round((exclusiveEnd - anniversary.getTime()) / MS_PER_DAY);
    const partial = remainingDays > 0 ? (remainingDays / 364) * annualRent : 0;

    return Math.round((fullYears * annualRent + partial) * 100) / 100;
  }

  // ── Month-precision path (legacy callers without dates) ──────────────────
  const contractMonths = term;
  if (contractMonths <= 0) return annualRent;
  const fullYears = Math.floor(contractMonths / 12);
  const remainingMonths = contractMonths - fullYears * 12;
  const total = fullYears * annualRent + (remainingMonths / 12) * annualRent;
  return Math.round(total * 100) / 100;
}

/**
 * Derive the canonical FULL-TERM total (ex-VAT) that the payment schedule, the
 * reconciliation target, the summary card, and the persisted `total_amount` must
 * ALL agree on. ONE implementation so the call sites can never diverge.
 *
 *   - rentBasis 'annual'     — the entered amount is the ANNUAL rate; prorate via
 *     `fullTermTotal` (full years at the annual rate + a REGA 364-day pro-rata
 *     slice of the final partial period). Prefers exact dates; falls back to the
 *     whole-month `contractMonths` proration when start/end aren't both present.
 *   - rentBasis 'term_total' — the entered amount IS the full-term total; as-is.
 *
 * IMPORTANT: the create wizard's amount field is the ANNUAL rent (auto-summed
 * from per-unit "الإيجار السنوي / Annual rent"), so creation MUST interpret it as
 * 'annual'. Treating it as 'term_total' bills a single year for multi-year leases
 * (2yr @ 10k/yr → 10k instead of 20k) and skips proration on partial terms.
 *
 * @param enteredAmount - Amount the user typed (annual rate or full-term total).
 * @param rentBasis     - How to interpret `enteredAmount`.
 * @param term          - Dates for day-precision proration; `contractMonths` is
 *                        the fallback used when start/end aren't both present.
 */
export function deriveCanonicalTotal(
  enteredAmount: number,
  rentBasis: "term_total" | "annual",
  term: { startDate?: string; endDate?: string; contractMonths: number },
): number {
  if (rentBasis === "annual") {
    return term.startDate && term.endDate
      ? fullTermTotal(enteredAmount, { startDate: term.startDate, endDate: term.endDate })
      : fullTermTotal(enteredAmount, term.contractMonths);
  }
  return enteredAmount;
}

/**
 * Contract term length in "year units" — fullYears + a fractional final
 * period (REGA 364-day proration for the date-precision path, or
 * remainingMonths/12 for the month-precision fallback).
 *
 * This mirrors the internals of `fullTermTotal()` EXACTLY (same anniversary
 * advance + exclusive-end day math) but returns the divisor instead of
 * multiplying it by an annual rent — `fullTermTotal(rent, term) ===
 * rent * termYearsEquivalent(term)`. Deliberately duplicated rather than
 * refactoring `fullTermTotal` to share this internally: that function is
 * already battle-tested (see the `fullTermTotal` describe block below), and
 * this keeps the blast radius of `deriveFirstYearRent()` (below) to zero for
 * the existing forward-direction callers.
 */
function termYearsEquivalent(term: number | { startDate: string; endDate: string }): number {
  if (typeof term === "object") {
    const { startDate, endDate } = term;
    if (!startDate || !endDate) return 1;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
      return 1;
    }

    const MS_PER_DAY = 1000 * 60 * 60 * 24;
    const exclusiveEnd = end.getTime() + MS_PER_DAY;

    let fullYears = 0;
    const anniversary = new Date(start);
    while (true) {
      const next = new Date(start);
      next.setFullYear(start.getFullYear() + fullYears + 1);
      if (next.getTime() <= exclusiveEnd) {
        fullYears += 1;
        anniversary.setFullYear(start.getFullYear() + fullYears);
      } else {
        break;
      }
    }

    const remainingDays = Math.round((exclusiveEnd - anniversary.getTime()) / MS_PER_DAY);
    const partial = remainingDays > 0 ? remainingDays / 364 : 0;

    return fullYears + partial;
  }

  const contractMonths = term;
  if (contractMonths <= 0) return 1;
  const fullYears = Math.floor(contractMonths / 12);
  const remainingMonths = contractMonths - fullYears * 12;
  return fullYears + remainingMonths / 12;
}

/**
 * Derive the FIRST-YEAR (annual) rent from whatever the user entered, for fee
 * bases that must always be quoted on one year's rent regardless of how the
 * contract amount was entered (CEO 2026-07-05: سعي/تسويق/إدارة العقود fees are
 * a % of the first year's rent, never the full multi-year total).
 *
 *   - rentBasis 'annual'     — enteredAmount already IS the annual rent;
 *     passthrough (mirrors deriveCanonicalTotal's forward direction).
 *   - rentBasis 'term_total' — enteredAmount is the FULL-TERM total; invert
 *     fullTermTotal()'s proration to recover the constant annual rate:
 *     annualRent = totalAmount / termYearsEquivalent(term). The divisor is
 *     clamped to >= 1 so a sub-year contract's first-year rent is never
 *     inflated above the entered total (a 6-month contract's "first year"
 *     rent is simply the whole amount, matching AC5's single-invoice case).
 *
 * @param enteredAmount - contracts.total_amount (term_total) or the annual
 *                        rate the user typed (annual).
 * @param rentBasis     - how to interpret enteredAmount (see deriveCanonicalTotal).
 * @param term          - dates for day-precision proration; contractMonths is
 *                        the fallback when start/end aren't both present.
 */
export function deriveFirstYearRent(
  enteredAmount: number,
  rentBasis: "term_total" | "annual",
  term: { startDate?: string; endDate?: string; contractMonths: number },
): number {
  if (!Number.isFinite(enteredAmount) || enteredAmount <= 0) return enteredAmount;
  if (rentBasis === "annual") return enteredAmount;

  const yearsEquivalent = Math.max(
    1,
    term.startDate && term.endDate
      ? termYearsEquivalent({ startDate: term.startDate, endDate: term.endDate })
      : termYearsEquivalent(term.contractMonths),
  );
  return Math.round((enteredAmount / yearsEquivalent) * 100) / 100;
}

// ── Flexible-payment VAT breakdown ─────────────────────────────────────────────

export interface PaymentVatBreakdown {
  /** Ex-VAT rent the user entered for this payment. */
  subtotal: number;
  /** Conditional VAT amount (0 when residential / rate 0). */
  vat: number;
  /** subtotal + vat. */
  total: number;
}

/**
 * Compute the VAT breakdown for ONE flexible payment row.
 *
 * The entered amount is treated as EX-VAT rent (the locked decision: users
 * enter rent ex-VAT; the system adds conditional VAT). `rentVatRate` is the
 * conditional rate — 0 for residential leases, 15 for commercial — derived
 * upstream from the unit/property purpose via `isRentVatApplicable`.
 *
 * Mirrors the backend regen-path math in `update_contract_with_invoices`
 * (20260531150000):
 *   vat   = ROUND(amount * rate / 100, 2)
 *   total = amount + vat
 */
export function paymentVatBreakdown(
  exVatAmount: number,
  rentVatRate: number,
): PaymentVatBreakdown {
  // Canonical formula lives in @dallali/shared (Fortress M2.3) — one VAT
  // formula FE/Nest. This wrapper keeps the established FE name/signature.
  return vatBreakdown(exVatAmount, rentVatRate);
}

export interface ScheduleVatTotals {
  /** Sum of ex-VAT rent across all rows (reconciles against the target). */
  subtotal: number;
  /** Sum of conditional VAT across all rows. */
  vat: number;
  /** subtotal + vat — the VAT-inclusive grand total. */
  total: number;
}

/**
 * Sum the ex-VAT subtotal, VAT, and inc-VAT grand total across a flexible
 * payment schedule. The `subtotal` is the figure that must reconcile with the
 * full-term target (same ex-VAT basis); `total` is shown separately as the
 * VAT-inclusive grand total.
 */
export function scheduleVatTotals(
  amounts: ReadonlyArray<number>,
  rentVatRate: number,
): ScheduleVatTotals {
  const acc = amounts.reduce(
    (sums, amount) => {
      const { subtotal, vat, total } = paymentVatBreakdown(Number(amount) || 0, rentVatRate);
      return { subtotal: sums.subtotal + subtotal, vat: sums.vat + vat, total: sums.total + total };
    },
    { subtotal: 0, vat: 0, total: 0 },
  );
  return {
    subtotal: Math.round(acc.subtotal * 100) / 100,
    vat: Math.round(acc.vat * 100) / 100,
    total: Math.round(acc.total * 100) / 100,
  };
}

/**
 * Per-unit service entry shapes the fold accepts. Mirrors the JSONB the create
 * RPC reads defensively: an object `{ amount, payer }` (modern) or a bare number
 * (legacy plain-number, implicitly tenant-paid). The wizard form-row's
 * `{ amount: string, payer }` is also accepted (amount coerced via Number()).
 */
export type ServiceFeeFold =
  | { amount: number | string | null | undefined; payer?: string | null }
  | number
  | null
  | undefined;

/** A unit's services bag — key → entry — as sent to / stored by the RPC. */
export interface UnitServiceFeesFold {
  service_fees?: Record<string, ServiceFeeFold> | null;
}

/**
 * Total tenant-paid annual service fees folded into rent invoices, computed to
 * MATCH the create RPC's per-payment rounding EXACTLY (20260609100000):
 *
 *   per_payment = ROUND(annual / paymentsCount, 2)        (per service, per unit)
 *   folded      = Σ_service_unit  per_payment × paymentsCount
 *
 * Only `payer === 'tenant'` entries with a positive amount are folded (owner-paid
 * services are EJAR-record-only and never invoiced). Bare-number entries are
 * treated as tenant-paid (legacy shape), matching the RPC.
 *
 * Because the RPC rounds each service share PER PAYMENT before summing, the
 * folded total can differ from the raw annual sum by a few halalas; reproducing
 * that rounding here keeps the header `total_amount` byte-identical to the sum of
 * the generated rent-invoice subtotals (the AC4 invariant).
 *
 * `paymentsCount` MUST be the number of generated payments (== paymentSchedule
 * length, == the RPC's `v_payments_count`). A non-positive count yields 0.
 */
export function deriveTenantPaidServicesTotal(
  units: ReadonlyArray<UnitServiceFeesFold> | null | undefined,
  paymentsCount: number,
): number {
  if (!units || units.length === 0) return 0;
  if (!Number.isFinite(paymentsCount) || paymentsCount <= 0) return 0;

  let totalHalalas = 0;
  for (const unit of units) {
    const bag = unit?.service_fees;
    if (!bag) continue;
    for (const entry of Object.values(bag)) {
      if (entry == null) continue;
      let amount: number;
      let payer: string;
      if (typeof entry === "number") {
        amount = entry;
        payer = "tenant";
      } else {
        amount = Number(entry.amount);
        payer = entry.payer ?? "tenant";
      }
      if (!Number.isFinite(amount) || amount <= 0 || payer !== "tenant") continue;
      // ROUND(amount / paymentsCount, 2) per the RPC, then × paymentsCount.
      const perPaymentHalalas = Math.round((amount / paymentsCount) * 100);
      totalHalalas += perPaymentHalalas * paymentsCount;
    }
  }
  return totalHalalas / 100;
}

/**
 * AUTHORITATIVE contract header `total_amount` — Fortress Rule 1 ("amounts are
 * derived, never received"). Recomputed from the server-trustworthy source rows
 * (the rent total the payment schedule sums to + the tenant-paid service fees the
 * invoices fold in), NOT from any client-supplied total field.
 *
 * The result equals the sum of the generated rent-invoice subtotals exactly:
 *   Σ invoice.subtotal = Σ rent_share (== rentTotal) + Σ services_share
 * so the contract header can never under-report vs the invoices again (the AC4
 * bug: commercial service fees were "saved but not counted" in the header total).
 *
 * VAT is intentionally NOT included — `total_amount` is the ex-VAT contract value
 * (rent + folded tenant services); VAT applies to rent only at invoice level and
 * is stored per-invoice, exactly as today.
 *
 * @param rentTotal     - Full-term rent total (== Σ paymentSchedule amounts).
 * @param units         - Per-unit payload carrying `service_fees` (same array sent
 *                        to the RPC). Single-unit/legacy contracts pass undefined.
 * @param paymentsCount - Number of generated payments (paymentSchedule length).
 */
export function deriveContractTotalAmount(
  rentTotal: number,
  units: ReadonlyArray<UnitServiceFeesFold> | null | undefined,
  paymentsCount: number,
): number {
  const rent = Number.isFinite(rentTotal) && rentTotal > 0 ? rentTotal : 0;
  const services = deriveTenantPaidServicesTotal(units, paymentsCount);
  return Math.round((rent + services) * 100) / 100;
}

/**
 * Compute a SALE contract's DOWN PAYMENT in SAR from the entered value +
 * interpretation (CEO 2026-07-23). A down payment can be a fixed SAR figure or
 * a percentage of the sale amount. The result is clamped to [0, saleAmount] so
 * a >100% or negative entry can never produce a negative balance. Returns 0 for
 * any non-finite/blank input.
 */
export function computeDownPayment(
  saleAmount: number,
  depositValue: number,
  downPaymentType: "fixed" | "percentage",
): number {
  const sale = Number.isFinite(saleAmount) && saleAmount > 0 ? saleAmount : 0;
  const raw = Number.isFinite(depositValue) && depositValue > 0 ? depositValue : 0;
  const amount = downPaymentType === "percentage" ? (sale * raw) / 100 : raw;
  const clamped = Math.min(Math.max(amount, 0), sale);
  return Math.round(clamped * 100) / 100;
}

/**
 * A SALE contract's SCHEDULED BALANCE — the amount that is actually invoiced to
 * the buyer after the down payment is DEDUCTED from the total sale amount
 * (spec item 2). `saleAmount` is the full negotiated price; `downPayment` is the
 * already-resolved SAR figure (see computeDownPayment). Clamped to ≥ 0.
 */
export function saleScheduledBalance(saleAmount: number, downPayment: number): number {
  const sale = Number.isFinite(saleAmount) && saleAmount > 0 ? saleAmount : 0;
  const down = Number.isFinite(downPayment) && downPayment > 0 ? downPayment : 0;
  return Math.round(Math.max(sale - down, 0) * 100) / 100;
}

// Sum the rent amounts across a list of unit rows for a multi-unit contract.
// Accepts both the wizard's form-row shape (rentAmount: string) and the
// junction-row shape (rent_amount: number) so the same helper can be used
// during creation AND when displaying a saved contract — no caller needs to
// reshape data before summing.
//
// Uses integer (halala) arithmetic to guarantee the result matches what
// Postgres numeric SUM computes from the same 2dp-rounded JSON values.
// Naïve IEEE 754 float addition can drift by a halala or two for certain
// per-unit amounts (e.g. 320000.01 + 320000.01 = 640000.02 in JS floats
// but Postgres sees exactly 640000.02 from the JSONB cast), triggering the
// RPC's SUM-of-units guard (#2483).
export function sumUnitRents(
  rows: ReadonlyArray<{ rentAmount?: string; rent_amount?: number | null }>
): number {
  const totalHalalas = rows.reduce((acc, row) => {
    const raw =
      row.rent_amount != null
        ? row.rent_amount
        : row.rentAmount
        ? Number(row.rentAmount)
        : 0;
    const value = Number.isFinite(raw) ? raw : 0;
    // Round to 2dp first so the halala count matches what Postgres will parse
    // from the JSONB payload (no trailing float noise beyond 2 decimal places).
    return acc + Math.round(value * 100);
  }, 0);
  return totalHalalas / 100;
}

/**
 * Split `total` evenly across `parts` so the per-part amounts (each at 2 dp)
 * sum back to `total` EXACTLY — no penny drift (#2487).
 *
 * Naïve `round(total / n)` per part overshoots/undershoots: 640000 / 6 =
 * 106666.6667 → 106666.67 × 6 = 640000.02. This floors each share to a halala
 * and lets the LAST part absorb the remainder, mirroring the contract rent-line
 * split convention (last unit absorbs). The contract RPC also reconciles
 * server-side, but sending an already-exact split keeps the per-unit values the
 * user sees correct.
 */
export function splitAmountEvenly(total: number, parts: number): number[] {
  if (parts <= 0) return [];
  const totalHalalas = Math.round(total * 100);
  const base = Math.floor(totalHalalas / parts);
  let allocated = 0;
  return Array.from({ length: parts }, (_, i) => {
    const halalas = i === parts - 1 ? totalHalalas - allocated : base;
    allocated += halalas;
    return halalas / 100;
  });
}

// ── Custom (flexible) schedule rehydration ──────────────────────────────────

/** Stored scheduled_payments row shape needed to rebuild a flexible schedule. */
export interface StoredScheduledPaymentRow {
  payment_number: number | null;
  due_date: string | null;
  amount: number | string | null;
}

/**
 * Rebuild the wizard's `customPaymentSchedule` from the contract's saved
 * `scheduled_payments` rows.
 *
 * THE BUG (CEO, 2026-06): editing a contract that was saved with a flexible /
 * custom payment table reopened the editor with an EMPTY table — the user had
 * to recreate every row by hand. `EditContractDrawer` hardcoded
 * `customPaymentSchedule: []` on hydration even though the frequency was
 * correctly recovered as "custom".
 *
 * `scheduled_payments.amount` is the pure EX-VAT rent per payment — exactly the
 * basis the `FlexiblePaymentEditor` expects (it adds conditional VAT on top) —
 * so it is the canonical source to round-trip the table. We deliberately read
 * the schedule rows, NOT the rent invoices, so the rebuild stays correct even
 * after services are folded into invoice subtotals.
 *
 * Rows are sorted by `payment_number` and re-numbered 1..N so the rebuilt array
 * matches the editor's contract (sequential, gap-free). `due_date` is
 * normalized to a `YYYY-MM-DD` string.
 */
export function rebuildCustomScheduleFromScheduledPayments(
  rows: ReadonlyArray<StoredScheduledPaymentRow>,
): { paymentNumber: number; dueDate: string; amount: number }[] {
  return [...rows]
    .filter((r) => r && r.due_date != null)
    .sort((a, b) => (a.payment_number ?? 0) - (b.payment_number ?? 0))
    .map((r, i) => ({
      paymentNumber: i + 1,
      dueDate: String(r.due_date).slice(0, 10),
      amount: Number(r.amount) || 0,
    }));
}

/** Normalize a stored fee-payer to "tenant"|"owner"; null/legacy → `fallback`, an explicit value is preserved. */
export function normalizeFeePayer(
  value: string | null | undefined,
  fallback: "tenant" | "owner",
): "tenant" | "owner" {
  return value === "tenant" || value === "owner" ? value : fallback;
}
